/** The only place API shapes become component props.
 *
 *  Every formatting decision and every wire-to-visual mapping lives here. No
 *  component and no screen reads a raw API field. When the contract and the
 *  design disagree, they disagree in this file and nowhere else.
 */

import type { WireCalendarEvent, WireEntry, WireHabit, EntryKind, EntryState } from './wire';

export type GlyphState =
  | 'open'
  | 'done'
  | 'cancelled'
  | 'migrated'
  | 'note'
  | 'question'
  | 'event';

export interface EntryToken {
  kind: 'stream' | 'project' | 'context';
  name: string;
}

export interface EntryRowProps {
  id: string;
  state: GlyphState;
  text: string;
  priority: boolean;
  tokens: EntryToken[];
  meta?: string;
  revision: number;
}

/** THE KIND/STATE TRAP.
 *
 *  The design's visual vocabulary has one glyph per `(kind, state)` pair, so
 *  `GlyphMark` and `EntryRow` take a single flattened `state` prop. The API has
 *  two separate fields. This function is the only legitimate place that collapse
 *  happens — never write `state === 'note'` in a component or a screen.
 *
 *  Order matters: a cancelled or migrated note is cancelled or migrated first.
 *  An answered question reads as done, because that is what the reader means.
 */
export function glyphStateFor(entry: { kind: EntryKind; state: EntryState }): GlyphState {
  if (entry.state === 'cancelled') return 'cancelled';
  if (entry.state === 'migrated') return 'migrated';
  if (entry.kind === 'note') return 'note';
  if (entry.kind === 'question') return entry.state === 'answered' ? 'done' : 'question';
  // No `event` branch: `EntryKind` is `task | note | question` and always has
  // been, so this could never fire. The design's `o event` glyph describes
  // *calendar* events, which are a separate resource with their own row — not a
  // journal entry. Generated types made the dead branch impossible to miss.
  return entry.state === 'done' ? 'done' : 'open';
}

const MONTHS = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];

/** `2026-08-02` → `02 AUG 2026`. Mono, day first, month in caps. Never `8/2/26`. */
export function displayDate(journalDate: string): string {
  const [y, m, d] = journalDate.split('-');
  const month = MONTHS[Number(m) - 1] ?? m ?? '';
  return `${d} ${month} ${y}`;
}

/** The day before, in journal-date form. Parsed as UTC so a local timezone
 *  cannot shift it across a boundary. */
export function previousDay(journalDate: string): string {
  const at = new Date(`${journalDate}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() - 1);
  return at.toISOString().slice(0, 10);
}

/** The day after, in journal-date form. */
export function nextJournalDay(journalDate: string): string {
  const at = new Date(`${journalDate}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + 1);
  return at.toISOString().slice(0, 10);
}

/** The `to` for `/v1/calendar/events`, given the last day you want included.
 *
 *  That parameter is an **exclusive** upper bound — the contract says so, and the
 *  service enforces `to - from >= 1`. Three call sites passed the last day they
 *  wanted, which meant the Monthly Log and the Future Log quietly dropped events
 *  on their final day, and Today — asking for one day as `from=X&to=X` — got a
 *  422 and no schedule at all, ever.
 *
 *  Named rather than inlined so the next caller reads the rule instead of
 *  guessing at it.
 */
export function calendarWindowEnd(lastDayInclusive: string): string {
  return nextJournalDay(lastDayInclusive);
}

/** `2026-08-02` → `SUN`. */
export function weekdayLabel(journalDate: string): string {
  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  // Parsed as UTC so a local timezone cannot shift the weekday.
  const at = new Date(`${journalDate}T00:00:00Z`);
  return days[at.getUTCDay()] ?? '';
}

/** Day of year, the way the daily log heads its page. */
export function dayOfYear(journalDate: string): number {
  const at = new Date(`${journalDate}T00:00:00Z`);
  const start = Date.UTC(at.getUTCFullYear(), 0, 0);
  return Math.floor((at.getTime() - start) / 86_400_000);
}

function parseContexts(contextsJson: string): string[] {
  if (!contextsJson) return [];
  try {
    const parsed: unknown = JSON.parse(contextsJson);
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
  } catch {
    return [];
  }
}

export function entryRow(
  entry: WireEntry,
  /** A token to leave off. On a filtered task list every row carries the token
   *  you filtered by, which is redundant ink on every line — the frame for
   *  `/Product` shows `#KovackLaunch @laptop` and no `/Product`. */
  without?: { kind: EntryToken['kind']; name: string },
): EntryRowProps {
  const keep = (token: EntryToken) =>
    !without || token.kind !== without.kind || token.name !== without.name;

  const tokens: EntryToken[] = [];
  if (entry.stream) tokens.push({ kind: 'stream', name: entry.stream });
  if (entry.project) tokens.push({ kind: 'project', name: entry.project });
  for (const name of parseContexts(entry.contexts_json)) {
    tokens.push({ kind: 'context', name });
  }

  // A migrated entry stays visible and names where it went.
  let meta: string | undefined;
  if (entry.state === 'migrated' && entry.continued_to_date) {
    meta = `→ ${displayDate(entry.continued_to_date)}`;
  } else if (entry.state === 'migrated' && entry.continued_to_month) {
    meta = `→ ${entry.continued_to_month}`;
  }

  return {
    id: entry.id,
    state: glyphStateFor(entry),
    text: entry.content,
    priority: entry.priority,
    tokens: tokens.filter(keep),
    meta,
    revision: entry.revision,
  };
}

/** Accent and monogram come from the design system, not from here.
 *
 *  An earlier version of this adapter reimplemented the hash and got a
 *  different one (`% ACCENTS.length` over a raw accumulator versus the design
 *  system's `% 9973`), which would have coloured calendars differently from the
 *  reference frames. The design system owns this mapping.
 */
import { accentFor, type CalendarAccent } from '../components/calendar/CalendarChip';

export function monogramFor(calendarName: string): string {
  const trimmed = calendarName.trim();
  return trimmed ? trimmed.slice(0, 2).toUpperCase() : '??';
}

export interface EventRowProps {
  id: string;
  time: string;
  title: string;
  calendar: string;
  monogram: string;
  accent: CalendarAccent;
}

export function eventRow(event: WireCalendarEvent): EventRowProps {
  return {
    id: event.id,
    time: event.all_day ? 'ALL DAY' : localTime(event.start),
    title: event.title,
    calendar: event.calendar_name,
    monogram: monogramFor(event.calendar_name),
    accent: accentFor(event.calendar_name),
  };
}

function localTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const hh = String(at.getHours()).padStart(2, '0');
  const mm = String(at.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export interface TrackerChipProps {
  id: string;
  name: string;
  count: number;
  goal: number;
  met: boolean;
}

export function trackerChip(habit: WireHabit, journalDate: string): TrackerChipProps {
  const count = habit.days.find((d) => d.journal_date === journalDate)?.count ?? 0;
  const goal = habit.goal_per_day || 1;
  return { id: habit.id, name: habit.name, count, goal, met: count >= goal };
}
